import {
  getSecurityTypesQueryKey,
  useSecurityTypes,
} from '@biasafe.queries/securities-classification-security-types/securities-classification-security-types';
import { SecurityTypesResponse } from '@biasafe.validators/securities-classification-security-types/securities-classification-security-types.zod';
import {
  STATIC_INDUSTRIES,
  STATIC_SECTORS,
  STATIC_SECURITY_TYPES,
  isSelectableSecurityType,
  STATIC_SUB_INDUSTRIES,
} from '../../config/constants/staticClassifications';

const TIME_24H = 24 * 60 * 60 * 1000;

const selectSecurityTypes = (data: unknown) =>
  SecurityTypesResponse.parse(data);

/**
 * The sector / industry-group / industry catalogues are STATIC, always, and are
 * no longer fetched.
 *
 * `/classification/{sectors,industries,sub-industries}` answers from a single
 * `SELECT DISTINCT` on the latest CLS snapshot, so it describes what exists
 * TODAY — 146 industries against the 316 the taxonomy has ever defined. Two
 * things break when the picker is built from that:
 *
 *   Backtests are point-in-time. A run starting in 2005 covers data where
 *   `10101001 Agricultural Inputs` existed, and the user could not exclude it
 *   because today's snapshot does not contain it.
 *
 *   GECS 2026 is announced but not yet in our feed. Cannabis and Crypto Mining
 *   are unselectable until it lands, so a universe silently GAINS them on
 *   migration day — the mirror of the renumbering hazard.
 *
 * The static catalogue is the comprehensive point-in-time union and is proven
 * complete: it equals the union of the three published GECS documents exactly
 * (see classification.py and test_classification_consistency.py). Dropping the
 * fetch also removes the degraded-vs-live divergence for these three lists —
 * there is now one list and one code path, so the offline fallback and the
 * healthy path render identically.
 *
 * What the universe actually holds today is NOT lost: it is surfaced as an
 * annotation (`universe*Codes` in useSecurityTypeClassification) rather than as
 * a filter, so the information guides the user without removing their choice.
 *
 * Security types are unaffected — that endpoint is a Python enum, not a
 * ClickHouse query, so it cannot go stale against history.
 */
export function useClassificationData() {
  const securityTypesQuery = useSecurityTypes({
    query: {
      queryKey: getSecurityTypesQueryKey(),
      staleTime: TIME_24H,
      gcTime: TIME_24H,
      retry: false,
      select: selectSecurityTypes,
    },
  });

  const securityTypes = securityTypesQuery.data;

  return {
    sectors: STATIC_SECTORS,
    industries: STATIC_INDUSTRIES,
    subIndustries: STATIC_SUB_INDUSTRIES,
    // The API returns the whole enum, so the filter has to apply to its
    // answer too — not just to the static fallback.
    securityTypes:
      securityTypes && securityTypes.length > 0
        ? securityTypes.filter(isSelectableSecurityType)
        : STATIC_SECURITY_TYPES,
    isLoading: securityTypesQuery.isLoading,
  };
}
